import { type Transaction, type Auth, NFTContractScheme } from 'oscar-typedef'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  type ITransactionExecutor,
  type EncUVD,
  SolanaPrepareData,
  type SolanaSendExtraData,
  type Nft,
} from '@/types'

export class SolanaNftTransactionExecutor implements ITransactionExecutor {
  private nft: Nft

  constructor(params: { nft: Nft }) {
    this.nft = params.nft
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<SolanaPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.nft.accountId,
          contract:
            this.nft.contract?.address?.toString() ?? this.nft.contractAddress,
          isNft: this.getStringSchema(),
          to: params.to,
          tokenId: this.nft.tokenId?.toString() ?? '',
          amount: params.amount,
        },
      )
    if (!data.detail) {
      throw new Error('No detail data for prepare')
    }
    return new SolanaPrepareData({
      blockhash: data.detail.blockhash,
      lamportsPerSignature: data.detail.lamportsPerSignature,
      lastValidBlockHeight: data.detail.lastValidBlockHeight,
      priorityFee: data.detail.priorityFee,
    })
  }

  private getStringSchema(): string {
    const scheme = this.nft.contract?.schema
    switch (scheme) {
      case NFTContractScheme.SOLANA_SFA:
      case NFTContractScheme.SOLANA_EDITION:
        return 'SPL'
      default:
        return 'SPL'
    }
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: SolanaSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    if (!this.nft.account) {
      throw new Error('Account is not found')
    }
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.nft.account.walletId,
      accountId: this.nft.accountId,
      pin: params.encUVD,
      contract: this.nft.contract?.address.toString(),
      tokenId: this.nft.id,
      isNft: this.getStringSchema(),
      txData: {
        txCommand: 'transfer-tkn',
        to: params.to,
        from: this.nft.account?.address,
        amount: params.amount,
        blockhash: params.extraData.blockhash,
        lastValidBlockHeight: params.extraData.lastValidBlockHeight,
        lamportsPerSignature: params.extraData.lamportsPerSignature,
        priorityFee: params.extraData.priorityFee,
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    if (!this.nft.account) {
      throw new Error('Account is not found')
    }
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.nft.account.walletId,
        accountId: this.nft.accountId,
        contract: this.nft.contract?.address.toString(),
        tokenId: this.nft.tokenId?.toString() ?? '',
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
        isNft: this.getStringSchema(),
      },
    )
  }
}
